import { CommonModule, NgIf } from '@angular/common'
import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { RouterModule } from '@angular/router'
import { CapitalizePipe } from '../../pipes/capitalize.pipe'
import { BlockUnblockComponent } from '../template/block-unblock/block-unblock.component'
import { DeleteComponent } from '../template/delete/delete.component'
import { PaginationComponent } from '../template/pagination/pagination.component'
import { NgSelectModule } from '@ng-select/ng-select'
import { map, Observable, Subject, takeUntil } from 'rxjs'
import { Store } from '@ngrx/store'
import { eps } from '../../services/url.service'
import { Role, RolePermission } from '../../store/role/role.state'
import { selectRoles, selectTotalCount } from '../../store/role/role.reducer'
import { Business } from '../../store/business/business.state'
import { selectAllBusinesses } from '../../store/business/business.selectors'
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { Actions, ofType } from '@ngrx/effects'
import {
  createRole,
  createRoleFailure,
  createRoleSuccess,
  loadRoles,
  updateRole,
  updateRoleFailure,
  updateRoleSuccess
} from '../../store/role/role.actions'
import { UtilsService } from '../../services/utils.service'
import { RoleService } from '../../services/role.service'
import { ToastrService } from 'ngx-toastr'
import { FormatModuleNamePipe } from '../../pipes/format-module-name.pipe'
import { loadBusinesses } from '../../store/business/business.actions'
import { HasPermissionDirective } from '../../directives/has-permission.directive'
import { noSpacesValidator } from '../../validators/no-spaces.validators'
import { noLeadingTrailingSpaces } from '../../validators/no-leadingTraillingSpace.validators'
import { SharedModule } from '../../shared/shared.module'
import { deleteRole } from '../../store/role/role.actions'

// interface ApiModule {
//   id: string
//   name: string
//   permissions: { action: string }[]
// }

interface PermissionModule {
  key: string
  name: string
  permissions: string[]
}

interface ApiPermissionPayload {
  moduleId: string
  permissionIds: string[]
}

// api-module.model.ts
export interface ApiPermission {
  id: string
  action: string
  moduleId?: string
}

export interface ApiModule {
  id: string
  name: string
  permissions: ApiPermission[]
  adminOnly?: boolean
  scopeType?: string
}

type PermissionAction = 'status' | 'view' | 'add' | 'edit' | 'delete'

type Permissions = Partial<Record<PermissionAction, boolean>>

@Component({
  selector: 'app-role-permission',
  imports: [
    ReactiveFormsModule,
    NgIf,
    CommonModule,
    RouterModule,
    CapitalizePipe,
    // BlockUnblockComponent,
    FormsModule,
    // DeleteComponent,
    PaginationComponent,
    NgSelectModule,
    FormatModuleNamePipe,
    HasPermissionDirective,
    SharedModule
  ],
  templateUrl: './role.component.html',
  styleUrl: './role.component.scss'
})
export class RoleComponent implements OnInit {
  private store = inject(Store) // Inject store using `inject()`
  toastr = inject(ToastrService)
  eps = eps
  loading$: Observable<boolean> = this.store.select(state => state.role.loading)
  error$: Observable<string | null> = this.store.select(state => state.role.error)
  pageLastRecord = 0
  totalCount$!: Observable<number>
  totalCount = 0
  roles$: Observable<Role[]> = this.store.select(selectRoles)
  currentPage = 1
  pageLimit = 10
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  private destroyed$ = new Subject<void>()
  readonly systemRoles = ['Admin', 'Business Owner', 'Employee', 'Contractor', 'Trainee']
  search = ''
  isStandard = false
  status = ''
  modules!: ApiModule[]
  permissionModules!: PermissionModule[]
  permissionsState: {
    [key: string]: Partial<Record<PermissionAction, boolean>>
  } = {}
  form!: FormGroup
  @ViewChild('addBusinessModals') addBusinessModals!: TemplateRef<any>
  private modalRef!: NgbModalRef
  modalTitle: any
  blockRoleId = ''
  businessId: string | null = null
  adminBusinessId$!: Observable<string | null | undefined>

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private actions$: Actions,
    private utilsService: UtilsService,
    private roleService: RoleService
  ) {
    // this.store.dispatch(resetRoleState())
    this.roles$ = this.store.select(selectRoles)
    this.store.dispatch(loadBusinesses({ params: null }))
  }

  ngOnInit() {
    this.loadRoles()
    this.loadModules()
    // this.totalCount$.subscribe(count => {
    //   this.totalCount = count
    //   this.updatePageLastRecord()
    // })

    this.totalCount$ = this.store.select(selectTotalCount).pipe(
      map(totalCount => totalCount || 0) // Default to 0 if totalCount is null
    )

    // Subscribe to totalCount$ safely
    this.totalCount$.subscribe(count => {
      this.totalCount = count // Define this.totalCount in your class
      this.updatePageLastRecord()
    })
    // this.totalCount = 10

    this.form = this.fb.group({
      businessIds: [{ value: [] }, Validators.required],
      name: ['', [Validators.required, Validators.maxLength(250), noLeadingTrailingSpaces()]],
      permissions: [''],
      isStandard: [false],
      isPermissionSets: ['Role']
    })

    this.actions$
      .pipe(
        ofType(createRoleSuccess, createRoleFailure, updateRoleSuccess, updateRoleFailure),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.form.reset({})
        this.blockRoleId = ''
        this.modalService.dismissAll() // or modalRef.close() if you're using one
        this.loadRoles()
        this.resetPermissions()
      })
  }

  loadRoles() {
    const params: any = {}
    // params.roleType = 'Role'
    // if (this.businessIdss?.length) {
    //   params.businessIds = this.businessIdss.join(',')
    // }

    // if (this.search) {
    //   params.name = this.search
    // }

    if (this.status) {
      const isDisabled = this.status === 'true' ? true : this.status === 'false' ? false : null
      params.disabled = isDisabled
    }

    if (this.isStandard) {
      params.isStandard = this.isStandard
    }

    if (this.businessId) {
      params.businessId = this.businessId
    }

    this.store.dispatch(
      loadRoles({
        params: new URLSearchParams(params).toString(),
        page: this.currentPage,
        limit: this.pageLimit
      })
    )
  }

  onSearch() {
    this.currentPage = 1 // Reset to the first page on search
    // const params = this.search ? `name=${encodeURIComponent(this.search)}` : null
    const params: any = {}
    if (this.search) {
      params.name = this.search
    }

    if (this.status) {
      const isDisabled = this.status === 'true' ? true : this.status === 'false' ? false : null
      params.disabled = isDisabled
    }

    if (this.isStandard) {
      params.isStandard = this.isStandard
    }

    if (this.businessId) {
      params.businessId = this.businessId
    }
    this.store.dispatch(
      loadRoles({
        params: new URLSearchParams(params).toString(),
        page: this.currentPage,
        limit: this.pageLimit
      })
    ) // Dispatch action with filter
  }

  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      this.loadRoles() // Reload businesses for the new page
      this.updatePageLastRecord()
    }
  }

  private updatePageLastRecord() {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  }

  onPageLimitChange() {
    this.currentPage = 1 // Reset to first page when limit changes
    this.loadRoles() // Load employees with new page limit
  }

  getDashboardRoute(): string {
    // return this.user?.role === 'Admin' ? eps.dashboardPath : eps.employeeDashboardPath
    return this.utilsService.getDashboardRoute()
  }

  loadModules() {
    this.roleService.modules().subscribe({
      next: response => {
        this.modules = response.modules
        this.permissionModules = this.mapApiModules(response.modules)
      },
      error: (error: { message: string }) => {
        this.toastr.error('Error: ' + error.message)
      }
    })
  }

  mapApiModules(apiModules: ApiModule[]): PermissionModule[] {
    return apiModules.map(m => ({
      key: m.name.toLowerCase().replace(/\s+/g, ''), // slugify name for key
      name: m.name,
      permissions: m.permissions.map(p => p.action)
    }))
  }

  getChecked(event: Event): boolean {
    const target = event.target as HTMLInputElement
    return target ? target.checked : false // Default to false if target is null
  }

  // togglePermission(module: any, action: string, value: boolean): void {
  //   const key = module.key
  //   if (!this.permissionsState[module.key]) {
  //     this.permissionsState[module.key] = {}
  //   }

  //   // Set the selected action
  //   this.permissionsState[key][action as PermissionAction] = value

  //   // Auto-check 'view' if 'add', 'edit', or 'delete' is selected
  //   if ((action === 'add' || action === 'edit' || action === 'delete') && value) {
  //     this.permissionsState[module.key]['view'] = true
  //   }
  // }

  togglePermission(module: any, action: string, value: boolean): void {
    const key = module.key
    if (!this.permissionsState[key]) {
      this.permissionsState[key] = {}
    }

    // Set the selected action
    this.permissionsState[key][action as PermissionAction] = value

    // Auto-check 'view' if 'add', 'edit', or 'delete' is selected
    if ((action === 'add' || action === 'edit' || action === 'delete') && value) {
      this.permissionsState[key]['view'] = true
    }

    // If 'view' is unchecked, uncheck 'add', 'edit', and 'delete'
    if (action === 'view' && !value) {
      this.permissionsState[key]['add'] = false
      this.permissionsState[key]['edit'] = false
      this.permissionsState[key]['delete'] = false
    }
  }

  onClose() {
    // if (this.form) {
    //   this.form.reset(); // Reset the form if it is defined
    // }
    // this.modalService.dismissAll();
    // this.currentStep = 1;

    try {
      this.blockRoleId = ''
      this.form.get('businessIds')?.enable({ emitEvent: false })
      this.resetPermissions()
      if (this.form) {
        this.form.reset()
      }
      if (this.modalRef) {
        this.modalRef.close()
      }
      this.modalService.dismissAll()
    } catch (error) {
      console.error('Error closing modal:', error)
    }
  }
  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    // console.log('control name', control)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null // No error
  }

  onSubmit() {
    const transformedPermissions = this.getTransformedPermissionsForApi()
    // console.log('transformed permissions', transformedPermissions)
    if (this.form.invalid) return

    // let roleType = ''
    // if (this.modalTitle === 'Create Role' || this.modalTitle === 'Edit Role') {
    //   roleType = 'Role'
    // }
    // if (
    //   this.modalTitle === 'Create Permission Sets' ||
    //   this.modalTitle === 'Edit Permission Sets'
    // ) {
    //   roleType = 'Permission Sets'
    //   this.isStandard = false
    // }
    if (this.form.get('isStandard')?.value === null || undefined) {
      this.isStandard = false
      this.form.get('isStandard')?.setValue(false)
    }
    if (this.form.valid && !this.blockRoleId) {
      this.store.dispatch(
        createRole({
          name: this.form.get('name')?.value,
          permissions: transformedPermissions, // ✅ stringify the object
          isStandard: this.form.get('isStandard')?.value,
          businessIds: this.form.get('businessIds')?.value
        })
      )
    } else if (this.form.valid && this.blockRoleId) {
      const businessIdsArray = this.form.get('businessIds')?.value || []

      // Convert array (even if single element) to string
      const businessIdsString = businessIdsArray.join(',')
      this.store.dispatch(
        updateRole({
          id: this.blockRoleId,
          name: this.form.get('name')?.value,
          permissions: transformedPermissions, // ✅ stringify the object
          isStandard: this.form.get('isStandard')?.value,
          businessId: businessIdsString
        })
      )
    }
    this.blockRoleId = ''
  }

  isChecked(permissions: string[], action: string): boolean {
    return permissions.includes(action)
  }

  toggleIsStandard() {
    this.isStandard = !this.isStandard

    // Set value from observable
    this.adminBusinessId$ = this.roles$.pipe(
      map(
        roles =>
          (Array.isArray(roles) ? roles.find(role => role.name === 'Admin') : undefined)?.businessId
      ),
      map(businessId => (businessId === null ? undefined : businessId)) // Normalizes null to undefined
    )

    // Subscribe ONCE and handle the form state
    this.adminBusinessId$.subscribe(businessId => {
      const control = this.form.get('businessId')
      control?.setValue(businessId)

      if (this.isStandard) {
        control?.disable()
      } else {
        control?.enable()
      }
    })

    // Set isStandard in the form as needed
    // if (this.modalTitle === 'Create Role' || this.modalTitle === 'Edit Role') {
    //   this.form.get('isStandard')?.setValue(this.isStandard);
    // } else {
    //   this.form.get('isStandard')?.setValue(false);
    // }
  }

  getTransformedPermissions(): Record<string, PermissionAction[]> {
    const result: Record<string, PermissionAction[]> = {}

    Object.entries(this.permissionsState).forEach(([moduleName, actions]) => {
      const allowedActions = Object.entries(actions)
        .filter(([_, isAllowed]) => isAllowed) // filter true permissions
        .map(([action]) => action as PermissionAction) // cast to proper type

      if (allowedActions.length > 0) {
        const camelCaseName = moduleName
        result[camelCaseName] = allowedActions
      }
    })

    return result
  }

  private toCamelCase(name: string): string {
    return name
      .replace(/[^a-zA-Z0-9 ]/g, '') // remove special characters
      .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
        index === 0 ? match.toLowerCase() : match.toUpperCase()
      )
      .replace(/\s+/g, '')
  }

  setPermissionsState(permissions: Record<string, string[]>) {
    Object.entries(permissions).forEach(([module, actions]) => {
      this.permissionsState[module] = {
        view: actions.includes('view'),
        add: actions.includes('add'),
        edit: actions.includes('edit'),
        delete: actions.includes('delete'),
        status: actions.includes('status')
      }
    })
  }

  // Returns whether all checkboxes for an action are checked
  // isAllChecked(action: 'view' | 'add' | 'edit' | 'delete'): boolean {
  //   return this.permissionModules.every(module => this.permissionsState[module.key]?.[action])
  // }

  isAllChecked(action: 'view' | 'add' | 'edit' | 'delete'): boolean {
    return (
      Array.isArray(this.permissionModules) &&
      this.permissionModules.length > 0 &&
      this.permissionModules.every(module => !!this.permissionsState[module.key]?.[action])
    )
  }

  // Toggles all checkboxes for a column based on the header checkbox
  // toggleAllColumn(action: 'view' | 'add' | 'edit' | 'delete', checked: boolean): void {
  //   for (const module of this.permissionModules) {
  //     if (!this.permissionsState[module.key]) {
  //       this.permissionsState[module.key] = {}
  //     }

  //     // Always check 'view' if action is add/edit/delete
  //     if (['add', 'edit', 'delete'].includes(action) && checked) {
  //       this.permissionsState[module.key]['view'] = true
  //     }

  //     this.permissionsState[module.key][action] = checked
  //   }
  // }

  toggleAllColumn(action: 'view' | 'add' | 'edit' | 'delete', checked: boolean): void {
    for (const module of this.permissionModules) {
      if (!this.permissionsState[module.key]) {
        this.permissionsState[module.key] = {}
      }

      if (action === 'view') {
        // Set view permission
        this.permissionsState[module.key]['view'] = checked

        // If view is unchecked, remove all add/edit/delete
        if (!checked) {
          this.permissionsState[module.key]['add'] = false
          this.permissionsState[module.key]['edit'] = false
          this.permissionsState[module.key]['delete'] = false
        }
      } else {
        // For add/edit/delete, always check view if checked is true
        if (checked) {
          this.permissionsState[module.key]['view'] = true
        }
        this.permissionsState[module.key][action] = checked
      }
    }
  }

  resetPermissions() {
    this.permissionsState = {}

    // Optional: reset all permissions to false
    this.permissionModules.forEach(module => {
      this.permissionsState[module.key] = {}
      module.permissions.forEach(action => {
        // this.permissionsState[module.key][action] = false
      })
    })
  }

  get isSystemRole(): boolean {
    const name = this.form.get('name')?.value
    // console.log('name', this.systemRoles.includes(name))
    return this.systemRoles.includes(name)
  }

  customSearchFn = (term: string, item: any) => {
    const fullName = `${item.businessName}`.toLowerCase()
    return fullName.includes(term.toLowerCase())
  }

  isAdmin(): boolean {
    return this.utilsService.isAdmin()
  }

  openBusinessModal(content: TemplateRef<any>, options: any = {}, title: string) {
    this.modalTitle = title
    this.modalRef = this.modalService.open(content, {
      scrollable: false,
      centered: true,
      size: 'lg',
      ...options
    })

    // Pre-fill form if in edit mode
    if (options) {
      this.blockRoleId = options.id
      //   this.setPermissionsState(options.permissions)
      //   this.setPermissions(options.rolePermissions)
      this.setPermissionsModule(this.modules, options.rolePermissions)

      // this.modalTitle = `Edit Role`
      this.modalTitle = title ?? 'Edit Role'

      // this.form.get('businessIds')?.setValue(options.businessId)
      this.form.patchValue({
        name: options.name,
        isStandard: options.isStandard,
        // businessId: [options.businessId],
        businessIds: Array.isArray(options.businessId) ? options.businessId : [options.businessId],
        permissions: options.permissions // assuming you have logic to map this
      })
      // To disable the control, call disable()
      if (this.blockRoleId) {
        this.form.get('businessIds')?.disable({ emitEvent: false })
      } else {
        this.form.get('businessIds')?.enable({ emitEvent: false })
      }
    } else {
      this.blockRoleId = ''
      this.form.reset() // if you're creating a new one
    }
  }

  getTransformedPermissionsForApi(): ApiPermissionPayload[] {
    const result: ApiPermissionPayload[] = []

    Object.entries(this.permissionsState).forEach(([moduleKey, actions]) => {
      const module =
        this.modules.find(m => m.name.toLowerCase() === moduleKey) ||
        this.modules.find(m => m.id === moduleKey)

      if (!module) return

      const allowedPermissionIds = Object.entries(actions)
        .filter(([_, isAllowed]) => isAllowed)
        .map(([action]) => {
          const permission = module.permissions.find(p => p.action === action)
          return permission ? permission.id : null
        })
        .filter(id => id !== null) as string[]

      if (allowedPermissionIds.length > 0) {
        result.push({
          moduleId: module.id,
          permissionIds: allowedPermissionIds
        })
      }
    })

    return result
  }

  setPermissions(rolePermissions: any) {
    // if (!role || !role.rolePermissions) {
    //   console.error('Invalid role object passed to setPermissions:', role)
    //   return
    // }

    this.permissionsState = {}
    this.permissionModules = []

    rolePermissions.forEach((rp: RolePermission) => {
      const moduleName = rp.permission.module.name.toLowerCase() // e.g., "dashboard"
      const action = rp.permission.action as PermissionAction // e.g., "view"

      // Initialize permissionsState for the module
      if (!this.permissionsState[moduleName]) {
        this.permissionsState[moduleName] = {}
        this.permissionModules.push({
          key: moduleName,
          name: rp.permission.module.name,
          permissions: ['view', 'add', 'edit', 'delete'] // all possible actions
        })
      }

      // Mark this action as true
      this.permissionsState[moduleName][action] = true
    })

    console.log('Final permissionsState:', this.permissionsState)
  }

  setPermissionsModule(modules: any, rolePermissions: any) {
    // if (!role || !role.rolePermissions) {
    //   console.error('Invalid role object passed to setPermissions:', role)
    //   return
    // }

    this.permissionsState = {}
    this.permissionModules = []

    // 1️⃣ Step 1: Initialize all modules from response.modules
    modules.forEach((mod: any) => {
      const moduleKey = mod.name.toLowerCase()

      // Create a default false state for each action
      this.permissionsState[moduleKey] = {
        view: false,
        add: false,
        edit: false,
        delete: false
      }

      // Add to permissionModules array for rendering in template
      this.permissionModules.push({
        key: moduleKey,
        name: mod.name,
        permissions: ['view', 'add', 'edit', 'delete']
      })
    })

    // 2️⃣ Step 2: Update permissionsState based on rolePermissions
    rolePermissions.forEach((rp: any) => {
      const moduleKey = rp.permission.module.name.toLowerCase()
      const action = rp.permission.action as PermissionAction

      if (this.permissionsState[moduleKey]) {
        this.permissionsState[moduleKey][action] = true
      }
    })

    // console.log('Final permissionsState:', this.permissionsState)
  }

  openDeleteModal(employeeId: string) {
    const modalRef = this.modalService.open(DeleteComponent)
    modalRef.componentInstance.recordId = employeeId
    modalRef.result.then(
      result => {
        // Handle the result from the modal
        if (result) {
          // Call the service to update the business status
          this.handleDeleteConfirm(result)
        }
      },
      reason => {
        // Handle the dismiss case
        if (reason) {
          this.handleDeleteConfirm(reason)
        }
      }
    )
  }

  handleDeleteConfirm(data: { id: string }) {
    this.store.dispatch(deleteRole({ id: data.id }))
  }

  onIsStandardChange() {
    this.currentPage = 1 // Reset to the first page on search
    this.loadRoles()
  }
}
